<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import * as echarts from 'echarts'
import { type EChartsType } from 'echarts'

interface Slice {
  name: string
  value: number
  color: string
}

const props = withDefaults(defineProps<{ data: Slice[]; heightClass?: string }>(), {
  heightClass: 'h-52',
})

const chartRef = ref<HTMLElement | null>(null)
let chartInstance: EChartsType | null = null

const fmt = (v: number) =>
  v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })

function initChart() {
  if (!chartRef.value) return

  const option = {
    tooltip: {
      trigger: 'item',
      formatter: (p: { marker: string; name: string; value: number; percent: number }) =>
        `${p.marker} ${p.name}<br/><strong>${fmt(p.value)}</strong> (${p.percent}%)`,
    },
    legend: {
      orient: 'vertical',
      right: '2%',
      top: 'middle',
      icon: 'circle',
      itemWidth: 10,
      itemHeight: 10,
      formatter: (name: string) => {
        const s = props.data.find((d) => d.name === name)
        return s ? `${name}  ·  ${fmt(s.value)}` : name
      },
    },
    series: [
      {
        type: 'pie',
        radius: ['52%', '78%'],
        center: ['33%', '50%'],
        avoidLabelOverlap: false,
        itemStyle: { borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { scale: true, scaleSize: 4 },
        data: props.data.map((d) => ({ value: d.value, name: d.name, itemStyle: { color: d.color } })),
      },
    ],
  }

  if (!chartInstance) chartInstance = echarts.init(chartRef.value)
  // notMerge: true — remplace toutes les séries à chaque maj (évite les tranches obsolètes)
  chartInstance.setOption(option, true)
}

function handleResize() {
  chartInstance?.resize()
}

onMounted(() => {
  initChart()
  window.addEventListener('resize', handleResize)
})

watch(() => props.data, () => initChart(), { deep: true })

onBeforeUnmount(() => {
  chartInstance?.dispose()
  window.removeEventListener('resize', handleResize)
})
</script>

<template>
  <div ref="chartRef" :class="['w-full', props.heightClass]"></div>
</template>
